import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { Permissions } from '@src/global/permissions';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';

import {
    CloneButton,
    TopToolbar,
    ListButton,
    DeleteButton,
    Button,
    ShowButton,
    useRecordContext,
    useResourceContext,
    useResourceDefinition,
} from 'react-admin';

import { RevisionHistoryPopup, ImpersonateButton } from '@src/custom';
import CreateModal from '@src/components/banners/CreateModal';
import sanitizeCloneRecord from '@src/utils/sanitizeCloneRecord';

const resourceToClassMap = {
    articles: 'Article',
    product_updates: 'ProductUpdate',
    alerts: 'Alert',
    underwriting_guidelines: 'Guide',
    program_guides: 'Guide',
    rate_bulletins: 'Guide',
    home_links: 'HomeLink',
    webinars: 'Webinar',
    trainings: 'Training',
    job_aids: 'JobAid',
    field_management_news: 'FieldManagementNews',
};

const CreateBannerActionButton = ({ record, resource, onClick }) => {
    const isLinkable = Object.keys(resourceToClassMap).includes(resource);
    const hasPermission = Permissions('promo_notification_settings', 'create') === true;

    if (!isLinkable || !hasPermission || !record) {
        return null;
    }

    return (
        <Button
            key="create-banner-btn"
            label="Create Banner"
            onClick={onClick}
        >
            <AddIcon />
        </Button>
    );
};

CreateBannerActionButton.propTypes = {
    record: PropTypes.object,
    resource: PropTypes.string,
    onClick: PropTypes.func,
};

const DeleteActionButton = ({ record, resource, hasDelete }) => {
    if (hasDelete && Permissions(resource, 'delete') === true) {
        return (
            <DeleteButton
                key="delete-btn"
                mutationMode="pessimistic"
                record={record}
                resource={resource}
            />
        );
    }

    return null;
};

DeleteActionButton.propTypes = {
    record: PropTypes.object,
    resource: PropTypes.string,
    hasDelete: PropTypes.bool,
};

const RevisionHistoryActionButton = ({ record, onClick }) => {
    if (record?.record_revisions?.length > 0) {
        return (
            <Button
                key="revisions-btn"
                label="Revision History"
                onClick={onClick}
            >
                <HistoryIcon />
            </Button>
        );
    }

    return null;
};

RevisionHistoryActionButton.propTypes = {
    record: PropTypes.object,
    onClick: PropTypes.func,
};

const CloneActionButton = ({ record, resource, hasClone }) => {
    if (Permissions(resource, 'create') === true && hasClone) {
        return (
            <CloneButton
                key="clone-btn"
                record={record}
            />
        );
    }

    return null;
};

CloneActionButton.propTypes = {
    record: PropTypes.object,
    resource: PropTypes.string,
    hasClone: PropTypes.bool,
};

const ImpersonateActionButton = ({ record, resource }) => {
    const list = ['agents', 'employees', 'admins'];
    if (list.includes(resource)) {
        return <ImpersonateButton record={record} />;
    }

    return null;
};

ImpersonateActionButton.propTypes = {
    record: PropTypes.object,
    resource: PropTypes.string,
};

const buildClonedData = (data) => {
    if (!data) {
        return {};
    }

    return sanitizeCloneRecord({
        ...data,
        status: 'draft',
        ...(data.title && { title: `${data.title} CLONED` }),
        ...(data.name && { name: `${data.name} CLONED` }),
        ...(data.slug && { slug: `${data.slug}-cloned` }),
    });
};

const buildBannerInitialResource = (data, resource) => ({
    id: data.id,
    title: data.title || data.name,
    type: resourceToClassMap[resource],
    status: data.status,
    published_at: data.published_at || data.publishedAt,
    expired_at: data.expired_at || data.expiredAt || data.contentExpiredAt || data.content_expired_at,
    state_ids: data.state_ids,
    states: data.states,
    sales_channel_ids: data.sales_channel_ids,
    sales_channels: data.sales_channels,
});

const RecordActionButtons = ({ data, resource, hasClone, hasDelete, onRevisionClick, onCreateBannerClick }) => {
    if (!data) {
        return null;
    }

    return (
        <>
            <DeleteActionButton
                record={data}
                resource={resource}
                hasDelete={hasDelete}
            />
            <RevisionHistoryActionButton
                record={data}
                onClick={onRevisionClick}
            />
            <CreateBannerActionButton
                record={data}
                resource={resource}
                onClick={onCreateBannerClick}
            />
            <CloneActionButton
                record={buildClonedData(data)}
                resource={resource}
                hasClone={hasClone}
            />
            <ImpersonateActionButton
                record={data}
                resource={resource}
            />
        </>
    );
};

RecordActionButtons.propTypes = {
    data: PropTypes.object,
    resource: PropTypes.string,
    hasClone: PropTypes.bool,
    hasDelete: PropTypes.bool,
    onRevisionClick: PropTypes.func,
    onCreateBannerClick: PropTypes.func,
};

const EditActions = (props) => {
    const {
        record,
        data: propData,
        resource: propResource,
        className,
        hasList: propHasList,
        hasShow: propHasShow,
        hasClone = true,
        hasDelete = true,
        listTo,
        listResource,
    } = props;
    const contextRecord = useRecordContext(props);
    const contextResource = useResourceContext(props);
    const { hasList, hasShow } = useResourceDefinition(props);
    const data = record || contextRecord || propData;
    const resource = propResource || contextResource;
    const shouldShowList = propHasList || hasList;
    const shouldShowShow = propHasShow || hasShow;
    const navigate = useNavigate();

    const [openRevisionsPopup, setOpenRevisionsPopup] = useState(false);
    const handleClick = () => setOpenRevisionsPopup(true);

    const handleClose = useCallback(() => {
        setOpenRevisionsPopup(false);
    }, []);

    const [openCreateBannerModal, setOpenCreateBannerModal] = useState(false);
    const handleCreateBannerClick = () => setOpenCreateBannerModal(true);
    const handleCloseCreateBannerModal = useCallback(() => {
        setOpenCreateBannerModal(false);
    }, []);

    const handleBannerCreated = useCallback((newBanner) => {
        navigate(`/banners/${newBanner.id}`);
    }, [navigate]);

    const hasRevisions = Boolean(data?.record_revisions?.length);

    // ListButton spreads unknown props over its own computed `to`, so passing
    // `to={undefined}` would blank out the link target.
    const listButtonProps = {
        ...(listTo ? { to: listTo } : {}),
        ...(listResource ? { resource: listResource } : {}),
    };

    return (
        <TopToolbar key={`toptoolbar-${data?.slug || ''}`} className={className}>
            <>
                <RecordActionButtons
                    data={data}
                    resource={resource}
                    hasClone={hasClone}
                    hasDelete={hasDelete}
                    onRevisionClick={handleClick}
                    onCreateBannerClick={handleCreateBannerClick}
                />
                {shouldShowList ? <ListButton key="list-btn" {...listButtonProps} /> : null}
                {shouldShowShow && data ? <ShowButton key="show-btn" record={data} /> : null}
                {hasRevisions ? (
                    <RevisionHistoryPopup
                        key="revision-history-popup"
                        modalOpen={openRevisionsPopup}
                        recordRevisions={data.record_revisions}
                        handleClose={handleClose}
                    />
                ) : null}
                {openCreateBannerModal ? (
                    <CreateModal
                        key="create-banner-modal"
                        initialResource={buildBannerInitialResource(data, resource)}
                        open={openCreateBannerModal}
                        onClose={handleCloseCreateBannerModal}
                        onCreated={handleBannerCreated}
                    />
                ) : null}
            </>
        </TopToolbar>
    );
};

EditActions.propTypes = {
    className: PropTypes.string,
    hasList: PropTypes.bool,
    hasShow: PropTypes.bool,
    record: PropTypes.object,
    data: PropTypes.object,
    resource: PropTypes.string,
    hasClone: PropTypes.bool,
    hasDelete: PropTypes.bool,
    listTo: PropTypes.string,
    listResource: PropTypes.string,
};

export default EditActions;
